import React, { useMemo, useState } from "react";
import ClusterTrendTable from "./ClusterTrendTable";
import StatCard from "./StatCard";
import TrendSparkline from "./TrendSparkline";
import UntrackedDemandPanel from "./UntrackedDemandPanel";
import { formatMetric, formatNumber } from "./clusterFormat";
import { useSeoSnapshots } from "./useSeoSnapshots";
import { computeClusterTrends, notableMovers, siteSeries, trendStyle } from "./seoTrends";
import { DEFAULT_RANGE, RANGES, rangeLabel, sliceToRange } from "./seoRanges";

/** Human date from a YYYY-MM-DD snapshot id. */
function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

const PerformancePanel = () => {
  // The hook fetches the full 2-year window once; the range only slices it, so
  // switching is instant and costs no extra reads.
  const { snapshots: allSnapshots, loading, error } = useSeoSnapshots();
  const [range, setRange] = useState(DEFAULT_RANGE);

  // All hooks must run before any early return, so slicing and trend maths sit
  // above the loading/error branches rather than beside their use sites.
  const snapshots = useMemo(() => sliceToRange(allSnapshots, range), [allSnapshots, range]);
  const trends = useMemo(() => computeClusterTrends(snapshots), [snapshots]);
  const site = useMemo(() => siteSeries(snapshots), [snapshots]);
  const latest = snapshots.length ? snapshots[snapshots.length - 1] : null;

  const rangePills = (
    <div className="flex flex-wrap gap-2">
      {RANGES.map((option) => (
        <button
          key={option.id}
          onClick={() => setRange(option.id)}
          className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
            range === option.id
              ? "bg-gray-800 text-white border-gray-800"
              : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return <p className="text-sm text-gray-500">Loading performance history…</p>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  // Checks the FULL set, not the slice: an empty range is a range problem and
  // is handled below, whereas an empty collection means nothing was captured.
  if (!allSnapshots.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <p className="text-sm text-gray-700 font-medium mb-1">No snapshots yet</p>
        <p className="text-sm text-gray-500">
          The weekly job captures the first data point on Monday at 7am Pacific. To populate
          history immediately, run{" "}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            node scripts/backfillSeoSnapshots.js
          </code>{" "}
          from <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">firebase/functions</code>.
        </p>
      </div>
    );
  }

  const movers = notableMovers(trends.clusters || [], 5);

  // Strategy progress, NOT share-of-traffic.
  //
  // The clusters are a deliberate narrow bet and were deployed recently, so
  // they start at zero by definition. The meaningful question is how many have
  // begun ranking — measuring what fraction of legacy traffic they capture
  // would score the plan against the very terms it chose to abandon.
  const tracked = latest?.trackedClusters ?? (latest ? Object.keys(latest.clusters || {}).length : 0);
  const live = latest?.clustersWithImpressions
    ?? Object.values(latest?.clusters || {}).filter((c) => (c.impressions || 0) > 0).length;
  const clusterImpressions = latest?.clusterImpressions
    ?? Object.values(latest?.clusters || {}).reduce((sum, c) => sum + (c.impressions || 0), 0);

  return (
    <div className="space-y-6">
      {/* ── Range selector ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {rangePills}
        <p className="text-xs text-gray-500">
          {snapshots.length} weekly point{snapshots.length === 1 ? "" : "s"} ·{" "}
          {formatDate(trends.from)} → {formatDate(trends.to)}
        </p>
      </div>

      {/* ── Headline figures ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* The figure is the latest WEEK; the sparkline spans the selected
            range. Labelled explicitly so the two are not read as the same. */}
        <StatCard label="Clicks (latest week)">
          <p className="text-2xl font-semibold text-gray-900">
            {formatNumber(latest?.site?.clicks)}
          </p>
          <TrendSparkline data={site.map((s) => s.clicks)} />
          <p className="text-[10px] text-gray-400 mt-0.5">trend over {rangeLabel(range).toLowerCase()}</p>
        </StatCard>

        <StatCard label="Impressions (latest week)">
          <p className="text-2xl font-semibold text-gray-900">
            {formatNumber(latest?.site?.impressions)}
          </p>
          <TrendSparkline data={site.map((s) => s.impressions)} />
          <p className="text-[10px] text-gray-400 mt-0.5">trend over {rangeLabel(range).toLowerCase()}</p>
        </StatCard>

        <StatCard label="Avg position">
          <p className="text-2xl font-semibold text-gray-900">
            {formatMetric(latest?.site?.avgPosition)}
          </p>
          {/* Position is inverted — lower is better, so the sparkline flips. */}
          <TrendSparkline data={site.map((s) => s.avgPosition)} invert color="#2563eb" />
        </StatCard>

        <StatCard label="Clusters ranking">
          <p className="text-2xl font-semibold text-gray-900">
            {live}
            <span className="text-base font-normal text-gray-400"> / {tracked}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {formatNumber(clusterImpressions)} impr from the plan
          </p>
        </StatCard>
      </div>

      {/* ── Not enough points to trend ───────────────────────────────────── */}
      {trends.insufficientData && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            {snapshots.length} point{snapshots.length === 1 ? "" : "s"} in the last{" "}
            {rangeLabel(range).toLowerCase()} — at least two are needed before a trend means
            anything. Pick a wider range, or wait for next Monday's capture.
          </p>
        </div>
      )}

      {/* ── Baseline context ─────────────────────────────────────────────── */}
      {/* Backfilled history predates the current clusters, so early weeks show
          near-zero cluster activity by construction. Saying so prevents the
          baseline being misread as decline. */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <span className="font-medium">Reading this screen:</span> history is backfilled from
          Search Console and reaches back before the current keyword plan existed. Early weeks
          therefore show little or no cluster activity — that is the baseline, not a drop. Judge
          the plan by whether <span className="font-medium">clusters ranking</span> and their
          impressions grow from here.
        </p>
      </div>

      {/* ── Movers ───────────────────────────────────────────────────────── */}
      {!trends.insufficientData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-3">
            Notable movement
          </p>
          {movers.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              No cluster moved beyond the noise threshold this period — that is a real result,
              not missing data.
            </p>
          ) : (
            <ul className="space-y-2">
              {movers.map((row) => {
                const { className, arrow } = trendStyle(row.positionTrend);
                return (
                  <li key={row.id} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-800">{row.id}</span>
                    <span className={className}>
                      {arrow} {formatMetric(row.positionBefore)} → {formatMetric(row.positionAfter)}
                      <span className="text-gray-500 ml-2">
                        ({formatNumber(row.impressionsBefore)} → {formatNumber(row.impressionsAfter)} impr)
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* ── Full cluster table ───────────────────────────────────────────── */}
      {!trends.insufficientData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-3">
            Clusters over time
          </p>
          <p className="text-xs text-gray-500 mb-3 -mt-2">
            Click a cluster to see the individual keywords behind these figures.
          </p>
          <ClusterTrendTable
            clusters={trends.clusters}
            fromDate={trends.from}
            toDate={trends.to}
          />
        </div>
      )}

      {/* ── Traffic outside the plan ─────────────────────────────────────── */}
      {/* Deliberately last: reference data about legacy demand, not a target.
          The clusters above are the thing being measured. */}
      <UntrackedDemandPanel snapshot={latest} />

      {/* Volume/KD provenance — see metricsAsOf in snapshotBuilder.js. */}
      {latest?.metricsAsOf && (
        <p className="text-xs text-gray-400">
          Clicks, impressions and position are measured per week from Search Console. Search
          volume and difficulty come from the DataForSEO refresh of {formatDate(latest.metricsAsOf)}{" "}
          and are not historical.
        </p>
      )}
    </div>
  );
};

export default PerformancePanel;
